if (!customElements.get("td-pack-modal")) {
  class TdPackModal extends HTMLElement {
    static FOCUSABLE_SELECTOR = [
      "button:not([disabled])",
      "[href]",
      "input:not([disabled]):not([type=hidden])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    constructor() {
      super();
      this.modal = this.querySelector(".td-pack-modal");
      this.overlay = this.querySelector(".td-pack-modal__overlay");
      this.closeTrigger = this.querySelector(".td-pack-modal__close");
      this.content = this.querySelector(".td-pack-modal__content");
      this._mutationObserver = null;
      this._focusFrame = null;
      this._lastFocusTrigger = null;
      this._onOpenEvent = this.onOpenEvent.bind(this);
      this._onKeyDown = this.onKeyDown.bind(this);
      this._onOverlayClick = () => this.closeModal();
      this._onCloseClick = () => this.closeModal();
    }
    connectedCallback() {
      if (!this.modal || !this.overlay || !this.closeTrigger) {
        console.log("TD Pack Modal: missing modal markup");
        return;
      }
      this.addEventListeners();
    }
    addEventListeners() {
      document.addEventListener("td:open-pack-modal", this._onOpenEvent);
      document.addEventListener("keydown", this._onKeyDown);
      this.overlay.addEventListener("click", this._onOverlayClick);
      this.closeTrigger.addEventListener("click", this._onCloseClick);
      const productForm = this.querySelector("td-product-form");
      if (productForm) {
        // td-product-form flags itself with is-success once the item is in the
        // cart; hide the modal then. The html lock is ours to release, but the
        // body lock stays for the cart drawer, which opens on this same cart
        // update and removes it when closed
        this._mutationObserver = new MutationObserver(() => {
          if (productForm.classList.contains("is-success") && this.isOpen()) {
            // Keep the body lock for the cart drawer, which opens on this same
            // cart update and removes it when closed.
            this.closeModal({ releaseBodyLock: false });
          }
        });
        this._mutationObserver.observe(productForm, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }
    disconnectedCallback() {
      document.removeEventListener("td:open-pack-modal", this._onOpenEvent);
      document.removeEventListener("keydown", this._onKeyDown);
      this.overlay?.removeEventListener("click", this._onOverlayClick);
      this.closeTrigger?.removeEventListener("click", this._onCloseClick);
      this._mutationObserver?.disconnect();
      this._mutationObserver = null;
      if (this._focusFrame !== null) {
        cancelAnimationFrame(this._focusFrame);
        this._focusFrame = null;
      }
      if (this.isOpen()) {
        this.closeModal({ restoreFocus: false });
      }
    }
    isOpen() {
      return this.modal?.getAttribute("data-modal-active") === "true";
    }
    onOpenEvent(e) {
      if (e.detail && e.detail.id === this.dataset.modalId) {
        this.openModal(e.detail.trigger);
      }
    }
    onKeyDown(e) {
      if (!this.isOpen()) return;
      if (e.key === "Escape") {
        this.closeModal();
        return;
      }
      if (e.key !== "Tab") return;

      const focusableElements = this.getFocusableElements();
      if (!focusableElements.length) return;

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      const focusIsOutsideModal = !this.modal.contains(document.activeElement);

      if (
        focusIsOutsideModal ||
        (e.shiftKey && document.activeElement === firstFocusable) ||
        (!e.shiftKey && document.activeElement === lastFocusable)
      ) {
        e.preventDefault();
        (e.shiftKey ? lastFocusable : firstFocusable).focus();
      }
    }
    getFocusableElements() {
      return this.modal
        ? [...this.modal.querySelectorAll(TdPackModal.FOCUSABLE_SELECTOR)]
        : [];
    }
    openModal(triggerEl) {
      this._lastFocusTrigger = triggerEl?.focus ? triggerEl : document.activeElement;
      if (this.content) {
        this.content.scrollTop = 0;
      }
      this.modal.setAttribute("data-modal-active", "true");
      this.modal.setAttribute("aria-hidden", "false");
      // html carries the viewport lock (body's overflow doesn't reach the
      // viewport because critical.css sets overflow-x: hidden on html);
      // body is locked too so the cart drawer handoff keeps working
      document.documentElement.classList.add("overflow-hidden");
      document.body.classList.add("overflow-hidden");
      this._focusFrame = requestAnimationFrame(() => {
        this._focusFrame = null;
        this.getFocusableElements()[0]?.focus();
      });
    }
    closeModal({ restoreFocus = true, releaseBodyLock = true } = {}) {
      if (this._focusFrame !== null) {
        cancelAnimationFrame(this._focusFrame);
        this._focusFrame = null;
      }
      this.modal.setAttribute("data-modal-active", "false");
      this.modal.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("overflow-hidden");
      if (releaseBodyLock) {
        document.body.classList.remove("overflow-hidden");
      }
      if (restoreFocus && this._lastFocusTrigger?.isConnected) {
        this._lastFocusTrigger.focus();
      }
      this._lastFocusTrigger = null;
    }
  }
  customElements.define("td-pack-modal", TdPackModal);

  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-pack-modal-trigger]");
    if (!trigger) return;
    // let add-to-cart clicks inside a clickable card go through untouched
    if (e.target.closest("form")) return;
    e.preventDefault();
    document.dispatchEvent(
      new CustomEvent("td:open-pack-modal", {
        detail: {
          id: trigger.getAttribute("data-pack-modal-trigger"),
          trigger,
        },
      })
    );
  });
}
